import React, { SyntheticEvent, useMemo } from 'react';

import cls from 'classnames';

import _noop from 'lodash/noop';

import ActivitiesLike from '@ott/activities-like';
import ImageLoader from '@ott/image-loader';
import Money from '@ott/money';

import prepareTags from '../../helpers/prepareTags';
import resolveResizedPhotoLink from '../../helpers/resolveResizedPhotoLink';
import trackKm from '../../helpers/trackKm';
import { CommonProps } from '../../types';
import Labels from '../Labels';
import LinkWrapper from '../LinkWrapper';
import Rating from '../Rating';

import styles from './ActivityCompactCard.scss';

const imageClassNames = { imageClass: styles.imageLoader };

type Props = CommonProps & {
  shouldShowTags?: boolean;
};

const ActivityCompactCard = ({
  activity,
  className,
  isExternalLink,
  onClick = _noop,
  onLikeUpdateSuccess = _noop,
  openInNewTab = false,
  prepareActivityLink,
  shouldShowLike = true,
  shouldShowTags = true,
  specificLocators,
  trackData,
}: Props) => {
  const {
    activityId,
    categories,
    coverPhoto = '',
    discount,
    favorite,
    price,
    rating,
    title,
    type,
    withoutDiscount,
  } = activity;

  const activityLink = useMemo(() => prepareActivityLink(activityId), [activityId]);

  const tags = useMemo(() => {
    return shouldShowTags ? prepareTags(categories) : [];
  }, [categories, shouldShowTags]);

  const handleLikeClick = (event: SyntheticEvent) => {
    event.preventDefault();
    event.stopPropagation();

    trackKm.onLikeClick({
      activity,
      isLikedBeforeClick: Boolean(favorite),
      trackData,
    });
  };

  return (
    <LinkWrapper
      className={cls(styles.container, className)}
      isExternalLink={isExternalLink}
      onClick={onClick}
      openInNewTab={openInNewTab}
      specificLocators={cls('activity-compact-card', specificLocators)}
      to={activityLink}
    >
      <ImageLoader
        src={resolveResizedPhotoLink(coverPhoto)}
        childrenClassNames={imageClassNames}
        isCropped={true}
      />

      <div className={styles.overlay} />
      <div className={styles.hOverlay} />

      <div className={styles.inner}>
        <div className={styles.grid}>
          <Labels
            className={styles.labels}
            type={type}
            discount={discount}
            categories={categories}
          />

          {shouldShowLike && (
            <ActivitiesLike
              activityId={activityId}
              className={styles.like}
              isLiked={favorite}
              onClick={handleLikeClick}
              onUpdateSuccess={onLikeUpdateSuccess}
            />
          )}
        </div>

        <div>
          <Rating className={styles.rating} rating={rating} />

          <div className={cls(styles.grid, styles.titlePriceRow)}>
            <div className={styles.title}>{title}</div>

            <div className={styles.prices}>
              <Money className={styles.price} amount={price} />
              {Boolean(withoutDiscount) && (
                <Money className={styles.withoutDiscount} amount={withoutDiscount} />
              )}
            </div>
          </div>

          <div className={styles.tags}>
            {tags.map((tag) => {
              return (
                <div className={styles.tag} key={tag}>
                  {tag}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </LinkWrapper>
  );
};

export default ActivityCompactCard;
